import React, { useState, useEffect, useCallback } from 'react';
import { Podcast, PodcastListResponse } from '../types/podcast';
import { useAuth } from '../contexts/AuthContext';
import Hero from '../components/Hero';
import PodcastCard from '../components/PodcastCard';
import PodcastPlayer from '../components/PodcastPlayer';
import './Podcasts.css';

const API_BASE = '/api';

const Podcasts: React.FC = () => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<Podcast | null>(null);

  const fetchPodcasts = useCallback(async (cursor?: string) => {
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (cursor) params.set('cursor', cursor);

      const res = await fetch(`${API_BASE}/podcasts?${params}`);
      if (!res.ok) throw new Error('Failed to load podcasts');

      const data: PodcastListResponse = await res.json();
      if (cursor) {
        setPodcasts(prev => [...prev, ...data.podcasts]);
      } else {
        setPodcasts(data.podcasts);
      }
      setNextCursor(data.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchPodcasts().finally(() => setLoading(false));
  }, [fetchPodcasts]);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) return;

    fetch(`${API_BASE}/podcasts/admin-status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => setIsAdmin(data.isAdmin === true))
      .catch(() => setIsAdmin(false));
  }, [user]);

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    await fetchPodcasts(nextCursor);
    setLoadingMore(false);
  };

  const handlePlay = (podcast: Podcast) => {
    if (currentlyPlaying?.podcastId === podcast.podcastId) {
      setCurrentlyPlaying(null);
    } else {
      setCurrentlyPlaying(podcast);
    }
  };

  const handleDelete = async (podcast: Podcast) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('You must be logged in to delete podcasts.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/podcasts/${podcast.podcastId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Delete failed' }));
        throw new Error(data.error || `Delete failed (${res.status})`);
      }

      // Stop playback if deleting the currently playing podcast
      if (currentlyPlaying?.podcastId === podcast.podcastId) {
        setCurrentlyPlaying(null);
      }
      setPodcasts(prev => prev.filter(p => p.podcastId !== podcast.podcastId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete podcast');
    }
  };

  return (
    <div className={`podcasts-page${currentlyPlaying ? ' has-player' : ''}`}>
      <Hero
        title="Podcasts"
        subtitle="AI-generated podcast conversations from our content"
      />

      <section className="podcasts-grid-section">
        <div className="podcasts-container">
          {loading && (
            <div className="podcasts-loading">
              <p>Loading podcasts...</p>
            </div>
          )}

          {error && !loading && (
            <div className="podcasts-error">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && podcasts.length === 0 && (
            <div className="podcasts-empty">
              <p>No podcasts yet. Check back soon!</p>
            </div>
          )}

          {!loading && !error && podcasts.length > 0 && (
            <>
              <div className="podcasts-grid">
                {podcasts.map(podcast => (
                  <PodcastCard
                    key={podcast.podcastId}
                    podcast={podcast}
                    isPlaying={currentlyPlaying?.podcastId === podcast.podcastId}
                    onPlay={handlePlay}
                    onDelete={isAdmin ? handleDelete : undefined}
                  />
                ))}
              </div>

              {nextCursor && (
                <div className="load-more-section">
                  <button
                    className="load-more-btn"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <PodcastPlayer
        podcast={currentlyPlaying}
        onClose={() => setCurrentlyPlaying(null)}
      />
    </div>
  );
};

export default Podcasts;
